import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Skeleton } from 'primeng/skeleton';
import { ClientChatSuggestionsStateService, TopicItem } from '../client-chat-suggestions-state.service';

/** Number of chips shown inline before overflow uses the More menu. */
const VISIBLE_COUNT = 3;

@Component({
  selector: 'bai-client-chat-suggestions',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Skeleton],
  templateUrl: './client-chat-suggestions.component.html',
  styleUrl: './client-chat-suggestions.component.scss',
})
export class ClientChatSuggestionsComponent {
  private readonly suggestionsState = inject(ClientChatSuggestionsStateService);

  sessionId = input<string | null>(null);

  /** Emits the selected topic so the parent can run the typewriter. */
  selectTopic = output<TopicItem>();

  private readonly sessionState = computed(() => {
    const id = this.sessionId();
    if (!id) return null;
    return this.suggestionsState.state()[id] ?? null;
  });

  readonly isLoading = computed(() => this.sessionState()?.loading ?? false);
  readonly hasError = computed(() => !!(this.sessionState()?.error));
  readonly errorMessage = computed(() => this.sessionState()?.error ?? '');
  readonly topics = computed(() => this.sessionState()?.topics ?? []);
  readonly generating = computed(() => this.sessionState()?.generating ?? false);
  readonly selectedTopicId = computed(() => this.sessionState()?.selectedTopicId ?? null);
  readonly isEmpty = computed(
    () => !this.isLoading() && !this.hasError() && !this.generating() && this.topics().length === 0
  );
  readonly showOverflow = signal(false);
  readonly visibleTopics = computed(() => this.topics().slice(0, VISIBLE_COUNT));
  readonly overflowTopics = computed(() => this.topics().slice(VISIBLE_COUNT));
  readonly hasOverflow = computed(() => this.overflowTopics().length > 0);
  readonly overflowLabel = computed(() => `+${this.overflowTopics().length} more`);

  onChipClick(topic: TopicItem): void {
    const sessionId = this.sessionId();
    if (!sessionId) return;
    this.suggestionsState.selectTopic(sessionId, topic.id);
    this.selectTopic.emit(topic);
  }

  onOverflowTopicClick(topic: TopicItem): void {
    this.onChipClick(topic);
    this.showOverflow.set(false);
  }

  toggleOverflow(): void {
    this.showOverflow.update((v) => !v);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(_event: MouseEvent): void {
    this.showOverflow.set(false);
  }
}
